import { EncChannel, EncField, Encoding, EncodingResult } from '@mode-vegas/vegas';
import { AccountFacade } from '@mode/accounts/contract';
import { SelectedChartService } from '@mode/charts/contract';
import { EncodingHelpers } from '@mode/charts/util-vegas';
import { ChartTypes, FlamingoTypes, OrgTypes } from '@mode/shared/contract-common';
import { AnalyticsService } from '@mode/shared/util-segment';
import { ToastService } from '@mode/shared/util-toast';
import { Observable, Subject, filter, map, takeUntil, withLatestFrom } from 'rxjs';
import { ZoneType } from '~/lib/chart-encoding-helpers';
import { AppStore } from '~/types/main.types';
import { MixableBaseController } from '../../../../common/component';
import { ChartObservables } from '../../../../types/viz-designer.types';
import { DropZone } from './builder-helpers';

/**
 * Builds:
 * Big Values
 */

class BigValuesEncodingBuilderController extends MixableBaseController {
  static $inject = ['RxStoreService', 'ToastService', 'AnalyticsService', 'AccountFacade'];

  dragCounter: number;
  dragHovered: EncChannel | 'field-list' | string | null;

  observables: ChartObservables;
  model: FlamingoTypes.ModeModel;
  view$: Observable<FlamingoTypes.ModeView>;
  encoding: Encoding;

  destroyed$ = new Subject<void>();
  queryToken$: Observable<string>;
  colorPaletteToken$: Observable<string>;
  currentPalette$: Observable<OrgTypes.ColorPalette>;
  allColorPalettes: OrgTypes.ColorPalette[] = [];
  chartService: SelectedChartService;

  canAcceptValues: boolean;

  rowZone: DropZone;
  textZone: DropZone;
  detailZone: DropZone;

  constructor(
    private rxStore: AppStore,
    private alertService: ToastService,
    private analyticsService: AnalyticsService,
    private accountFacade: AccountFacade
  ) {
    super();

    this.accountFacade.reportOwnerColorPalettes$.pipe(takeUntil(this.destroyed$)).subscribe((palettes) => {
      this.allColorPalettes = [...palettes]
        .filter((palette) => palette.type === OrgTypes.ColorPaletteType.Divergent)
        .sort((a, b) => a.name.localeCompare(b.name));
    });
  }

  $onInit() {
    this.dragCounter = 0;
    this.queryToken$ = this.observables.queryToken$;
    this.view$ = this.observables.view$;
    this.colorPaletteToken$ = this.observables.colorPaletteToken$;
    this.currentPalette$ = this.colorPaletteToken$.pipe(
      withLatestFrom(this.accountFacade.reportOwnerColorPalettes$),
      map(([token, palettes]) => palettes.find((p) => p.token === token)),
      filter((palette) => palette != null)
    );

    this.textZone = new DropZone(
      'Value',
      'text',
      this.chartService,
      this.alertService,
      this.analyticsService,
      this.observables.chartType$,
      this.destroyed$,
      undefined,
      undefined,
      (encField) => this.removeField(encField)
    );

    this.detailZone = new DropZone(
      'Trend',
      'detail',
      this.chartService,
      this.alertService,
      this.analyticsService,
      this.observables.chartType$,
      this.destroyed$,
      undefined,
      () => this.chartService.dragField.source === 'field-list',
      (encField) => this.removeField(encField)
    );

    this.rowZone = new DropZone(
      'Group By',
      'row',
      this.chartService,
      this.alertService,
      this.analyticsService,
      this.observables.chartType$,
      this.destroyed$,
      undefined,
      undefined,
      (encField) => this.removeField(encField)
    );
  }

  $onChanges(changes: angular.IOnChangesObject) {
    if (changes.encoding && this.encoding != null) {
      this.canAcceptValues = this.text.length <= 0;
    }
  }

  $onDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  removeField($field: EncField) {
    const encResult = EncodingHelpers.removeFieldFromEncoding(this.encoding, 'bigValue', $field.id); // can use any chart type in the shape
    this.chartService.dispatchEncodingUpdate(encResult);
  }

  fieldUpdated(result: EncodingResult) {
    this.chartService.dispatchEncodingUpdate(result, ChartTypes.PillActionType.ChangePillFormula);
  }

  removeSort($source: string) {
    this.chartService.removeSort($source);
  }

  onAttrSelected($event, view: FlamingoTypes.ModeView, channel: EncChannel, zone: ZoneType) {
    const { event, attr } = $event;
    let dropzone = this.textZone;

    if (channel === 'row') {
      dropzone = this.rowZone;
    } else if (channel === 'detail') {
      dropzone = this.detailZone;
    } else {
      dropzone = this.textZone;
    }

    const newEncoding = dropzone.addField(view, this.encoding, attr, undefined, zone);

    if (newEncoding != null) {
      this.chartService.dispatchEncodingUpdate(newEncoding);
    }
  }

  onPaletteAssigned(palette: OrgTypes.ColorPalette) {
    this.chartService.updateTheme('', palette.token, palette.value.split(','), true);
  }

  get hasRow(): boolean {
    return this.row.length > 0;
  }

  get hasDetail(): boolean {
    return this.detail.length > 0;
  }

  get hasText(): boolean {
    return this.text.length > 0;
  }

  get detail() {
    return (this.encoding && this.encoding.marks.properties.detail.values) || [];
  }

  get row() {
    return (this.encoding && this.encoding.y) || [];
  }

  get text() {
    return (this.encoding && this.encoding.marks.properties.text.values) || [];
  }

  get showColorZone() {
    // Color requires an indicator field be present
    return this.showIndicatorZone && this.hasDetail;
  }

  get showIndicatorZone() {
    // Indicator requires value and trend by fields be present
    return this.hasText && this.hasRow;
  }
}

export const BigValuesEncodingBuilderComponent: angular.IComponentOptions = {
  bindings: {
    model: '<',
    encoding: '<',
    observables: '<',
    chartService: '<',
  },
  controller: BigValuesEncodingBuilderController,
  controllerAs: 'ctrl',
  template: `

  <!-- Value -->
  <div
    id="text"
    class="card-item"
    ng-class="{'drag-over': ctrl.textZone.hoverZone === 'text'}"
    ng-dragenter="ctrl.textZone.dragEnter($event)"
    ng-dragover="ctrl.textZone.dragOver($event)"
    ng-dragleave="ctrl.textZone.dragLeave($event)"
    ng-dragend="ctrl.textZone.dragLeave($event)"
    ng-drop="ctrl.textZone.fieldDropped($event, ctrl.view$ | async:this, ctrl.encoding, null, 'AGGREGATE')">
      <div class="header">Value</div>
      <div class="drop-area">
          <ul class="list-unstyled">
            <li ng-repeat="field in ctrl.text">
              <enc-view-calc-field
                field="field"
                view="ctrl.view$ | async:this"
                on-update="ctrl.fieldUpdated($result)"
                on-remove="ctrl.removeField($field)"
                model="ctrl.model"
                view-name="ctrl.queryToken$ | async:this"
                encoding="ctrl.encoding"
                allow-scalars="false"
                allow-aggregates="true"
                allow-discrete="false"
                allow-continuous="true"
                on-drag-start="ctrl.textZone.dragStart($event, $field, $valid)"
                on-drag-cancel="ctrl.textZone.dragEnd($event, 'text', $field)" />
            </li>
          </ul>
          <mode-typeahead-attrs-list
            [view]="ctrl.view$ | async:this"
            [chart-type]="ctrl.observables.chartType$ | async:this"
            [placeholder-text]="ctrl.text.length === 0 ? 'Add a field here...' : 'Replace field...'"
            (attr-selected)="ctrl.onAttrSelected($event, ctrl.view$ | async:this, 'text', 'AGGREGATE')"
          ></mode-typeahead-attrs-list>
      </div>
  </div>
  <!-- Trend By -->
  <div id="row"
    class="card-item"
    ng-if="ctrl.hasText"
    ng-class="{'drag-over': ctrl.rowZone.hoverZone === 'row'}"
    ng-dragenter="ctrl.rowZone.dragEnter($event)"
    ng-dragover="ctrl.rowZone.dragOver($event)"
    ng-dragleave="ctrl.rowZone.dragLeave($event)"
    ng-dragend="ctrl.rowZone.dragLeave($event)"
    ng-drop="ctrl.rowZone.fieldDropped($event, ctrl.view$ | async:this, ctrl.encoding, null, 'DISCRETE')">
    <div class="header">Trend By</div>
    <div class="drop-area">
      <enc-view-calc-field
        ng-if="ctrl.view$ | async:this"
        ng-repeat="field in ctrl.row"
        on-update="ctrl.fieldUpdated($result)"
        on-remove="ctrl.removeField($field)"
        on-remove-sort="ctrl.removeSort($source)"
        allow-scalars="true"
        allow-aggregates="false"
        allow-discrete="true"
        allow-continuous="false"
        field="field"
        model="ctrl.model"
        view-name="ctrl.queryToken$ | async:this"
        view="ctrl.view$ | async:this"
        encoding="ctrl.encoding"
        on-drag-start="ctrl.rowZone.dragStart($event, $field, $valid)"
        on-drag-cancel="ctrl.rowZone.dragEnd($event, 'row', $field)" />
      <mode-typeahead-attrs-list
        [view]="ctrl.view$ | async:this"
        [chart-type]="ctrl.observables.chartType$ | async:this"
        [placeholder-text]="ctrl.row.length === 0 ? 'Add a field here...' : 'Replace field...'"
        (attr-selected)="ctrl.onAttrSelected($event, ctrl.view$ | async:this, 'row', 'DISCRETE')"
      ></mode-typeahead-attrs-list>
    </div>
  </div>
  <!-- Indicator -->
  <div
    id="detail"
    class="card-item"
    ng-if="ctrl.hasText && ctrl.hasRow"
    ng-class="{'drag-over': ctrl.detailZone.hoverZone === 'detail'}"
    ng-dragenter="ctrl.detailZone.dragEnter($event)"
    ng-dragover="ctrl.detailZone.dragOver($event)"
    ng-dragleave="ctrl.detailZone.dragLeave($event)"
    ng-dragend="ctrl.detailZone.dragLeave($event)"
    ng-drop="ctrl.detailZone.fieldDropped($event, ctrl.view$ | async:this, ctrl.encoding, null, 'AGGREGATE')">
      <div class="header">Indicator</div>
      <div class="drop-area">
          <ul class="list-unstyled">
            <li ng-repeat="field in ctrl.detail">
              <enc-view-calc-field
                field="field"
                on-update="ctrl.fieldUpdated($result)"
                on-remove="ctrl.removeField($field)"
                model="ctrl.model"
                view-name="ctrl.queryToken$ | async:this"
                view="ctrl.view$ | async:this"
                encoding="ctrl.encoding"
                allow-scalars="false"
                allow-aggregates="true"
                allow-quick-calcs="true"
                allow-discrete="false"
                allow-continuous="true"
                on-drag-start="ctrl.detailZone.dragStart($event, $field, $valid)"
                on-drag-cancel="ctrl.detailZone.dragEnd($event, 'detail', $field)" />
            </li>
          </ul>
          <mode-typeahead-attrs-list
            [view]="ctrl.view$ | async:this"
            [chart-type]="ctrl.observables.chartType$ | async:this"
            [placeholder-text]="ctrl.detail.length === 0 ? 'Add a field here...' : 'Replace field...'"
            (attr-selected)="ctrl.onAttrSelected($event, ctrl.view$ | async:this, 'detail', 'AGGREGATE')"
          ></mode-typeahead-attrs-list>
      </div>
  </div>
  <div class="card-item color-selector" ng-if="ctrl.showColorZone">
    <mode-color-palette-assignment
      [selected-color-palette]="ctrl.currentPalette$ | async:this"
      [all-color-palettes]="ctrl.allColorPalettes"
      (palette-assigned)="ctrl.onPaletteAssigned($event)"
    ></mode-color-palette-assignment>
  </div>
`,
};
